/**
 * HearingReportViewerModal — read-only viewer for a saved hearing-report
 * snapshot. Mounts <ReportsPanel> with `hideBuilder={true}` and hydrates
 * all state from the snapshot JSON so the report re-renders exactly as
 * it was saved.
 *
 * Print: uses `window.print()` scoped to the modal's DOM tree via a
 * body-class + @media print rule so we don't accidentally print the
 * live editor behind the modal.
 *
 * Props:
 *   versionId : string
 *   onClose   : ()  → void
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Printer } from 'lucide-react';
import ReportsPanel from './ReportsPanel';
import { useAuth } from '../AuthContext';

import useEscapeKey from '../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HearingReportViewerModal({ versionId, onClose }) {
  const { user } = useAuth();
  useEscapeKey(onClose);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  // Split / Combined chooser lives in the toolbar so the audiologist can
  // switch the audiogram layout AFTER opening a saved report. Default =
  // Split (matches the clinic-house style used by the live preview).
  // Bug 3 (2026-02-17) — previously hard-coded to "combined" with no
  // user affordance.
  const [audiogramMode, setAudiogramMode] = useState('separate');

  useEffect(() => {
    if (!versionId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/hearing-reports/${versionId}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || 'Could not load saved report');
      }
    })();
    return () => { alive = false; };
  }, [versionId]);

  // Toggle a body class so @media print CSS hides everything outside the
  // modal → the browser print dialog only sees the archived report.
  useEffect(() => {
    document.body.classList.add('printing-past-report');
    return () => document.body.classList.remove('printing-past-report');
  }, []);

  // Bug 5 · Sync the split-view print CSS scoping class to the current
  // audiogram mode so Print never spills onto page 2 in Split.
  useEffect(() => {
    if (audiogramMode === 'separate') {
      document.body.classList.add('printing-report-split');
    } else {
      document.body.classList.remove('printing-report-split');
    }
    return () => document.body.classList.remove('printing-report-split');
  }, [audiogramMode]);

  const handlePrint = () => {
    window.print();
  };

  if (!versionId) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 flex flex-col past-report-viewer"
      data-testid="hearing-report-viewer-modal"
    >
      {/* Toolbar — NOT printed */}
      <div className="past-report-viewer-toolbar bg-slate-900 text-white px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-sm font-bold truncate">
            {data?.label || 'Saved Report'}
          </div>
          {data?.patient_name && (
            <div className="text-xs text-slate-300 truncate">
              · {data.patient_name}
              {data.patient_mrd ? ` · ${data.patient_mrd}` : ''}
            </div>
          )}
          <div className="text-[10px] font-bold uppercase tracking-wide bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded ml-2">
            View-only
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Audiogram layout toggle — user can pick Split / Combined
              on any saved report (Bug 3, 2026-02-17). */}
          <div
            className="inline-flex rounded overflow-hidden border border-white/20 text-[10px] font-semibold"
            data-testid="hearing-report-viewer-audiogram-toggle"
            role="group"
            aria-label="Audiogram layout"
          >
            <button
              type="button"
              onClick={() => setAudiogramMode('separate')}
              aria-pressed={audiogramMode === 'separate'}
              data-testid="hearing-report-viewer-audiogram-split"
              className={`px-2 py-1.5 transition ${
                audiogramMode === 'separate'
                  ? 'bg-white text-slate-900'
                  : 'bg-transparent text-white hover:bg-white/10'
              }`}
            >
              Split
            </button>
            <button
              type="button"
              onClick={() => setAudiogramMode('combined')}
              aria-pressed={audiogramMode === 'combined'}
              data-testid="hearing-report-viewer-audiogram-combined"
              className={`px-2 py-1.5 transition ${
                audiogramMode === 'combined'
                  ? 'bg-white text-slate-900'
                  : 'bg-transparent text-white hover:bg-white/10'
              }`}
            >
              Combined
            </button>
          </div>
          <button
            type="button"
            onClick={handlePrint}
            data-testid="hearing-report-viewer-print"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-white text-slate-900 hover:bg-slate-100"
          >
            <Printer size={13} /> Print
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="hearing-report-viewer-close"
            className="w-8 h-8 rounded hover:bg-white/10 flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto bg-slate-100">
        {!data && !err && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-slate-400" />
          </div>
        )}
        {err && (
          <div className="p-8 text-center text-sm text-rose-600 font-semibold">{err}</div>
        )}
        {data && (
          <ReportsPanel
            patient={data.snapshot?.patient || {}}
            rightEarData={data.snapshot?.right_ear_audiogram || {}}
            leftEarData={data.snapshot?.left_ear_audiogram || {}}
            preTestData={data.snapshot?.pre_test_data || {}}
            impedanceData={data.snapshot?.impedance_data || {}}
            speechData={data.snapshot?.speech_data || {}}
            specialTestsData={data.snapshot?.special_tests_data || {}}
            oaeData={data.snapshot?.oae_data || {}}
            soundfieldData={data.snapshot?.soundfield_data || {}}
            abrData={data.snapshot?.abr_data || {}}
            pediatricData={data.snapshot?.pediatric_data || {}}
            tinnitusData={data.snapshot?.tinnitus_data || {}}
            sessionId={data.snapshot?.session?.session_id}
            audiologistName={data.snapshot?.audiologist?.name || user?.name || ''}
            // 2026-02 · Fallback to the current logged-in user's
            // user_id so legacy snapshots (pre-2026-02, before
            // TestSession.audiologist_user_id existed) still render
            // the signature + license via SignatureFooter's fetch.
            // Post-2026-02 snapshots always carry the real signing
            // audiologist's user_id from the enriched snapshot.
            audiologistUserId={data.snapshot?.audiologist?.user_id || user?.user_id || ''}
            clinicalImpression={data.snapshot?.builder?.clinical_impression || ''}
            recommendations={data.snapshot?.builder?.recommendations || []}
            audiogramMode={audiogramMode}
            initialBuilder={data.snapshot?.builder || {}}
            hideBuilder
            previewId="report-preview-past"
          />
        )}
      </div>
    </div>
  );
}
